import { useEffect, useState, useContext } from 'react'
import { NextPage } from 'next'
import {
  getEntitlementClaims,
  postUnlockRequest,
} from '../services/EntitlementClient'
import { Entitlement } from '../types/types'
import EntitlementList from '../components/Entitlement/EntitlementList'
import Spinner from '../components/Spinner'
import { NondescriptError } from '../components/NondescriptError'
import EntitlementInformation from '../components/Entitlement/EntitlementInformation'
import { UserContext } from '../components/context'
import * as Sentry from '@sentry/nextjs'

const AuthorizedComputers: NextPage = () => {
  const [entitlements, setEntitlements] = useState<Entitlement[]>([])
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const reasonAccount = useContext(UserContext)

  const handleUnlockRequest = (hardwareId: string): Promise<void> => {
    try {
      if (!reasonAccount) {
        console.error('handleUnlockRequest: no account info found')
        setError(true)
      }

      return postUnlockRequest({
        hardwareId,
        email: reasonAccount?.email,
        username: reasonAccount?.username,
      })
    } catch (err) {
      console.error(err)
      setError(true)

      return Promise.reject()
    }
  }

  const isActive = (entitlement: Entitlement) => {
    return new Date(entitlement.expiryDate) > new Date()
  }

  const fetchEntitlments = async () => {
    try {
      const claims = await getEntitlementClaims()
      claims?.length && setEntitlements(claims.filter(isActive))
    } catch (err) {
      Sentry.captureException(err)
      setError(true)
    }
  }

  useEffect(() => {
    setLoading(true)
    fetchEntitlments().then(() => setLoading(false))
  }, [])

  if (loading) {
    return <Spinner />
  }

  if (!loading && error) {
    return <NondescriptError />
  }

  return (
    <>
      <div className="mb-60">
        <EntitlementInformation />
        <div className="mb-20">
          {entitlements.length ? (
            <EntitlementList
              entitlements={entitlements}
              handleUnlockRequest={handleUnlockRequest}
            />
          ) : (
            <p>
              <i>No computers have been long-term authorized</i>
            </p>
          )}
        </div>
      </div>
      <div className="mb-60">
        <h2 className="h4">Legacy Authorizations</h2>
        <hr />
        <p>
          The information above is for Reason 12.6 or later. <br /> Visit your{' '}
          <a
            href="https://www.reasonstudios.com/user/products"
            target="_blank"
            rel="noreferrer"
          >
            product page
          </a>{' '}
          to manage computer authorization for earlier versions of Reason.
        </p>
      </div>
    </>
  )
}

export default AuthorizedComputers
